import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { CustomDatePicker, todayCalendarDate, calendarDateToEpochMillis } from '@medprompt/shared';
import { getReferralTypeClass } from './ReferralModal';

const REFERRAL_TYPE_OPTIONS = [
  { value: 'TWO_WEEK_WAIT', label: '2-WEEK WAIT' },
  { value: 'URGENT', label: 'URGENT' },
  { value: 'ROUTINE', label: 'ROUTINE' }
];

const CreateReferralModal = ({ isOpen, onClose, onCreate, loading }) => {
  const [nhsId, setNhsId] = useState('');
  const [text, setText] = useState('');
  const [referralType, setReferralType] = useState('ROUTINE');
  const [referredAt, setReferredAt] = useState(() => todayCalendarDate());
  const [errors, setErrors] = useState({});

  const reset = () => {
    setNhsId('');
    setText('');
    setReferralType('ROUTINE');
    setReferredAt(todayCalendarDate());
    setErrors({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validate = () => {
    const newErrors = {};
    const trimmedNhsId = nhsId.replace(/\s/g, '');
    if (!trimmedNhsId) {
      newErrors.nhsId = 'NHS number is required';
    } else if (trimmedNhsId.length !== 10) {
      newErrors.nhsId = 'NHS number must be 10 digits';
    }
    if (!text.trim()) {
      newErrors.text = 'Notes are required';
    }
    if (!referredAt) {
      newErrors.referredAt = 'Referral date is required';
    }
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const referredAtEpochMillis = calendarDateToEpochMillis(referredAt);

    onCreate({
      nhsId: nhsId.replace(/\s/g, ''),
      text: text.trim(),
      referralType,
      referredAt: referredAtEpochMillis
    });
    reset();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-animated create-referral-modal">
        <div className="modal-header">
          <h2>Add Case to Referral Tracker</h2>
          <button className="close-button" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-content">
          <div className="create-referral-field">
            <label className="create-referral-label">NHS Number *</label>
            <input
              type="text"
              className={`create-referral-input ${errors.nhsId ? 'referral-input-error' : ''}`}
              value={nhsId}
              onChange={(e) => {
                setNhsId(e.target.value);
                if (errors.nhsId) setErrors((prev) => ({ ...prev, nhsId: null }));
              }}
              placeholder="e.g. 943 476 5919"
              maxLength={12}
            />
            {errors.nhsId && <span className="create-referral-error">{errors.nhsId}</span>}
          </div>

          <div className="create-referral-field">
            <label className="create-referral-label">Referral Type *</label>
            <div className="create-referral-type-group">
              {REFERRAL_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`referral-type-pill ${referralType === opt.value ? 'selected' : ''} ${getReferralTypeClass(opt.value)}`}
                  onClick={() => setReferralType(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className={`create-referral-field ${errors.referredAt ? 'create-referral-field-error' : ''}`}>
            <label className="create-referral-label">Date of Referral *</label>
            <CustomDatePicker
              label=""
              value={referredAt}
              onChange={(dateStr) => {
                setReferredAt(dateStr);
                if (errors.referredAt) setErrors((prev) => ({ ...prev, referredAt: null }));
              }}
            />
            {errors.referredAt && <span className="create-referral-error">{errors.referredAt}</span>}
          </div>

          <div className="create-referral-field">
            <label className="create-referral-label">Notes *</label>
            <textarea
              className={`create-referral-textarea ${errors.text ? 'referral-input-error' : ''}`}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (errors.text) setErrors((prev) => ({ ...prev, text: null }));
              }}
              placeholder="Enter referral notes..."
              rows={4}
            />
            {errors.text && <span className="create-referral-error">{errors.text}</span>}
          </div>
        </div>
        <div className="modal-actions">
          <button className="cancel-button" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button className="header-button header-button-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Create Referral'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateReferralModal;
